import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, imageUrl } from "../lib/api";
import LogoMark from "../components/LogoMark";
import ProjectForm from "../components/admin/ProjectForm";

const LoginView = ({ onDone }) => {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/login", { mobile, password });
      onDone(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF9F6] px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-3xl border border-[#E7E3D8] bg-white p-8 shadow-[0_16px_44px_rgba(14,32,56,0.08)] sm:p-10"
      >
        <img
          src="/shivaay-logo.png"
          alt="Shivaay Consultant logo"
          className="h-12 w-12 rounded-xl object-cover ring-1 ring-gold/40"
        />
        <h1 className="mt-6 font-serif text-3xl font-semibold text-navy">Inventory Manager</h1>
        <p className="mt-2 text-sm leading-relaxed text-navy/60">
          Sign in with your registered mobile number to add, edit or remove the
          listings shown on the landing page.
        </p>
        <input
          data-testid="admin-login-mobile"
          type="tel"
          required
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          placeholder="Mobile number"
          className="mt-8 w-full rounded-xl border border-[#E7E3D8] bg-ivory px-4 py-3 text-sm text-navy outline-none transition-colors placeholder:text-navy/35 focus:border-gold-deep"
        />
        <input
          data-testid="admin-login-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="mt-3 w-full rounded-xl border border-[#E7E3D8] bg-ivory px-4 py-3 text-sm text-navy outline-none transition-colors placeholder:text-navy/35 focus:border-gold-deep"
        />
        <button
          data-testid="admin-login-submit"
          disabled={busy}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy-mid disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const AdminPanel = ({ user, onLogout }) => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(undefined);

  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/api/projects")).data,
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/api/projects/${id}`),
    onSuccess: () => {
      toast.success("Project removed from the website");
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const logout = async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      onLogout();
    }
  };

  const remove = (p) => {
    if (window.confirm(`Remove "${p.name}" from the website?`)) del.mutate(p.id);
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E] pb-24 text-[#F8FAFC]">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0B0C0E]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" />
            <span className="font-serif text-lg font-semibold text-[#F3E5AB]">Inventory Manager</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              data-testid="admin-view-site-link"
              className="hidden items-center gap-1.5 text-sm text-[#94A3B8] transition-colors hover:text-[#F3E5AB] sm:flex"
            >
              <ExternalLink className="h-4 w-4" />
              View site
            </a>
            <span className="hidden text-xs text-[#64748B] md:block">{user.email}</span>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-[#CBD5E1] transition-colors hover:border-[#D4AF37]/50 hover:text-[#F3E5AB]"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10">
        <div className="flex items-center justify-between">
          <h1 className="font-serif text-3xl font-semibold">
            Projects{" "}
            <span className="text-[#64748B]">({projects.data?.length ?? "…"})</span>
          </h1>
          <button
            data-testid="admin-add-project"
            onClick={() => setEditing(null)}
            className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-bold text-[#0B0C0E] transition-colors hover:bg-[#F3E5AB]"
          >
            <Plus className="h-4 w-4" />
            Add Project
          </button>
        </div>

        {editing !== undefined && (
          <ProjectForm project={editing} onClose={() => setEditing(undefined)} />
        )}

        {projects.isLoading ? (
          <div className="mt-10 flex justify-center" data-testid="admin-loading">
            <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
          </div>
        ) : (projects.data || []).length === 0 ? (
          <p className="mt-10 text-center text-sm text-[#8B93A5]" data-testid="admin-empty">
            No projects yet — click "Add Project" to publish your first one.
          </p>
        ) : (
          <div className="mt-8 space-y-4">
            {projects.data.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-project-row-${p.id}`}
                className="flex flex-col gap-4 rounded-2xl border border-white/8 bg-[#13151B] p-4 sm:flex-row sm:items-center"
              >
                <img
                  src={imageUrl(p.image) || "/favicon.svg"}
                  alt={p.name}
                  className="h-24 w-full rounded-xl object-cover sm:w-36"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-xl font-semibold text-[#F8FAFC]">{p.name}</p>
                  <p className="mt-0.5 truncate text-sm text-[#94A3B8]">
                    {p.location} · {p.config || "—"} · {p.price || "—"}
                  </p>
                  <p className="mt-1 text-xs uppercase tracking-wider text-[#D4AF37]">
                    {p.status}
                    {p.featured && (
                      <span className="ml-2 rounded-full border border-[#D4AF37]/50 px-2 py-0.5 text-[10px] font-bold text-[#D4AF37]" data-testid={`admin-priority-${p.id}`}>
                        PRIORITY
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid={`admin-edit-${p.id}`}
                    onClick={() => setEditing(p)}
                    className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-[#CBD5E1] transition-colors hover:border-[#D4AF37]/50 hover:text-[#F3E5AB]"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    data-testid={`admin-delete-${p.id}`}
                    onClick={() => remove(p)}
                    disabled={del.isPending}
                    className="flex items-center gap-1.5 rounded-full border border-red-500/30 px-4 py-2 text-sm text-red-400 transition-colors hover:bg-red-500/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api
      .get("/api/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0C0E]">
        <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" data-testid="admin-session-check" />
      </div>
    );
  }
  if (!user) return <LoginView onDone={setUser} />;
  return <AdminPanel user={user} onLogout={() => setUser(null)} />;
}
